import { useAsync } from '@tiger/hooks';
import { Button, Tag } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';
import Card from 'components/Card';
import { useConfirmModal } from 'components/Modal';
import SwitchAccountButton from 'components/SwitchAccountButton';
import Toast from 'components/Toast';
import Track from 'components/Track';
import {
    applyOrRevokeDeleteApplication,
    deleteIBSubmitClose,
    getDeleteStatus,
    getIBCloseStatus
} from 'modules/Logoff/request';
import security from 'stores/security';
import { UserProcessStatus } from 'types/customer';
import './style.scss';

export const statusTextMap: Record<UserProcessStatus, string> = {
    UNACCOUNT: __('未开户'),
    FACE: __('开户中'),
    CONTINUE: __('开户中'),
    INVALID: __('审核失败'),
    AUDITING: __('审核中'),
    ACTIVATING: __('激活中'),
    OPENED: __('开户成功'),
    FUNDED: __('已入金'),
    CLOSED: __('账户已注销')
};

const Header: React.FC = observer(() => {
    const { webAccountInfo, currentProcessStatus, userInfo, missingBindEmailAndPhone } = security;
    const inviteCode = userInfo!.inviteCode;

    const { open } = useConfirmModal();

    const [_, getLogoffData] = useAsync(async () =>
        Promise.all([
            getDeleteStatus().then(hasApplied => {
                security.setHasAppliedDelete(hasApplied);

                return hasApplied;
            }),
            getIBCloseStatus().then(closeStatus => {
                security.setCloseStatus(closeStatus);

                return closeStatus;
            })
        ])
    );

    const handleCancel = () => {
        open({
            title: __('Do you want to proceed with your request?'),
            okText: __('Confirm'),
            className: 'cancel-close-modal',
            onOk: async () => {
                try {
                    if (security.hasAppliedDelete) {
                        const data = await applyOrRevokeDeleteApplication('revoke');
                        const { success, reasons } = data || {};

                        if (success) {
                            Toast.show(__('Submitted successfully'));
                        } else {
                            Toast.show(reasons[0].content);
                        }
                    } else {
                        await deleteIBSubmitClose();

                        Toast.show(__('Submitted successfully'));

                        getLogoffData();
                    }
                } catch (error) {
                    Toast.show(error);
                }
            }
        });
    };

    return (
        <Card className="profile-header">
            <div className="header-content">
                <div className="header-switch">
                    <h2 className="current-account">{__('交易账户')}</h2>
                    <SwitchAccountButton />
                </div>
                <div className="header-info">
                    <div>
                        <div className="label-item">
                            <p className="label label-bold">
                                {__('姓名：')}
                                {webAccountInfo?.real_name || '--'}
                            </p>
                        </div>
                        {/* 如果缺少邮箱和手机号, 则不展示邀请码 */}
                        {!missingBindEmailAndPhone && (
                            <div className="label-item">
                                <p className="label">
                                    {__('邀请码：')}
                                    {inviteCode}
                                </p>
                                <Track eventModel="首页导航" eventType="导航" eventName="邀请好友模块">
                                    <Link to="invitation">
                                        <Button size="small">{__('查看邀请')}</Button>
                                    </Link>
                                </Track>
                            </div>
                        )}
                    </div>
                </div>
                <div className="header-status">
                    <div>
                        <div className="header-status-top">
                            <p>
                                {__('账户状态：')} {statusTextMap[currentProcessStatus]}
                            </p>
                            {
                                // marsco账户关户、销户不存在取消请求。
                                !security.isMarscoAccount &&
                                    (security.hasAppliedDelete || security.closeStatus === 'PENDING') && (
                                        <Button className="btn-cancel-close" type="link" onClick={handleCancel}>
                                            {__('Logoff:Cancel')}
                                        </Button>
                                    )
                            }
                        </div>
                        {/* clearing="ib"  时，取 ib_account 字段 clearing="marsco" 时，取 br_id 字段*/}
                        {(webAccountInfo?.br_id || webAccountInfo?.ib_account) && (
                            <p className="d-flex align-items-center">
                                {__('header:账户号码：')}
                                {webAccountInfo.clearing === 'ib' ? webAccountInfo?.ib_account : webAccountInfo.br_id}
                                {webAccountInfo.br_account_type && (
                                    <Tag className="ml-1" color="gold">
                                        {webAccountInfo.br_account_type}
                                    </Tag>
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
});

export default Header;
